import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import {
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';
import OfficeClocks from '../components/OfficeClocks';

const contactInfo = [
  {
    icon: EnvelopeIcon,
    title: 'Email us',
    content: 'sales@truelog.com.sg',
    href: 'mailto:sales@truelog.com.sg',
  },
  {
    icon: PhoneIcon,
    title: 'Singapore main office',
    content: '+65 6909 3756',
    href: 'tel:+6569093756',
  },
  {
    icon: ClockIcon,
    title: 'Business hours',
    content: 'Monday to Friday, 9:00 AM to 6:00 PM in each office, across five time zones',
  },
];

const offices = [
  {
    country: 'Singapore',
    office: 'Headquarters',
    address: '101 Cecil Street #11-05\nSingapore 069533',
    phone: '+65 6909 3756',
    flag: '🇸🇬',
    city: 'Singapore',
    timeZone: 'Asia/Singapore',
  },
  {
    country: 'Vietnam',
    office: 'TrueLog Vietnam',
    address: 'CT Building, 56 Yen The\nHo Chi Minh City',
    phone: '+84 397 337 372',
    flag: '🇻🇳',
    city: 'Ho Chi Minh City',
    timeZone: 'Asia/Ho_Chi_Minh',
  },
  {
    country: 'India',
    office: 'TrueLog India',
    address: 'Chennai',
    phone: '+91 979 091 6352',
    flag: '🇮🇳',
    city: 'Chennai',
    timeZone: 'Asia/Kolkata',
  },
  {
    country: 'Belgium',
    office: 'TrueLog Europe',
    address: 'Antwerp\nBelgium',
    phone: '+32 456 390 341',
    flag: '🇧🇪',
    city: 'Antwerp',
    timeZone: 'Europe/Brussels',
  },
  {
    country: 'UAE',
    office: 'TrueLog Middle East Services FZCO',
    address: 'Mina Jebel Ali, Jafza 4, Lobby 0403\nDubai, United Arab Emirates',
    phone: '+65 6909 3756',
    flag: '🇦🇪',
    city: 'Dubai',
    timeZone: 'Asia/Dubai',
  },
  {
    country: 'Sri Lanka',
    office: 'TrueLog Sri Lanka',
    address: '385, Kollupitiya Galle Road\nColombo 03, Sri Lanka',
    phone: '+65 6909 3756',
    flag: '🇱🇰',
    city: 'Colombo',
    timeZone: 'Asia/Colombo',
  },
];

const quickAnswers = [
  {
    question: 'What services do you offer?',
    answer:
      'We provide trade compliance (IOR/EOR), global freight, bonded warehousing and fulfilment, and reverse logistics across 140+ countries.',
  },
  {
    question: 'Do you handle customs and IOR requirements?',
    answer:
      'Yes. TrueLog provides structured customs support and IOR execution across regulated markets so customers can enter new countries without unnecessary friction.',
  },
  {
    question: 'Can you support urgent rollout timelines?',
    answer:
      'Yes. We design execution around deployment windows, SLA requirements, and site-level constraints rather than treating logistics as a generic shipment task.',
  },
];

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');

  // Posts to the inventory app's public endpoint (same origin on beta; proxied by
  // nginx on truelog.com.sg). Every accepted enquiry appears on Org 52 → Website Enquiries.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/website/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...formData,
          enquiry_type: formData.service,
          website: honeypot,
          page_path: window.location.pathname,
          referrer: document.referrer || '',
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) {
        const fieldErrors = payload.errors ? Object.values(payload.errors as Record<string, string>).join(' ') : '';
        throw new Error(fieldErrors || payload.error || 'Something went wrong. Please email sales@truelog.com.sg.');
      }
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
    } catch (err) {
      setSubmitError(
        err instanceof Error && err.message
          ? err.message
          : 'We could not send your enquiry. Please email sales@truelog.com.sg.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((current) => ({
      ...current,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Contact"
        title="Start with the rollout challenge, not the shipment form."
        highlight="rollout challenge"
        description="If customs, fragmented vendors, poor visibility, or unpredictable handoffs are getting in the way, start the conversation there. TrueLog can help shape the operating model around the actual problem."
        heroIcon={EnvelopeIcon}
        accentIcon={GlobeAltIcon}
        badges={[
          'Talk through scope and countries',
          'Pressure-test the operating model',
          'Map the next rollout phase',
        ]}
        metrics={[
          { value: '24/7', label: 'Operational support mindset' },
          { value: '140+', label: 'Countries supported' },
          { value: '6', label: 'Global offices' },
        ]}
        panelEyebrow="Direct access"
        panelTitle="Reach the team that plans and executes the work"
        panelDescription="Use the form for a scoped enquiry, or reach out directly if you already know the markets and timing involved."
        panelItems={contactInfo.map(({ icon, title, content }) => ({
          icon,
          title,
          description: content,
        }))}
      />

      <section className="bg-slate-50 py-16">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Offices Right Now"
            title="Six offices, five time zones"
            description="From Ho Chi Minh City to Antwerp, our teams hand over to each other as the day moves west, so there is almost always a TrueLog office at work."
          />
          <OfficeClocks
            offices={offices.map(({ city, country, timeZone }) => ({ city, country, timeZone }))}
          />
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom grid gap-8 lg:grid-cols-[1.04fr_0.96fr]">
          <div>
            <SectionHeading
              eyebrow="Get In Touch"
              title="Tell us where execution is getting complicated"
              description="Share the countries, device types, deployment windows, or compliance requirements involved. The more operational detail you provide, the more useful the first response will be."
              align="left"
            />

            <div className="rounded-[34px] border border-slate-200/80 bg-white p-7 shadow-[0_28px_70px_-42px_rgba(15,23,42,0.28)]">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex min-h-[460px] flex-col items-center justify-center text-center"
                >
                  <div className="inline-flex rounded-full bg-emerald-100 p-4 text-emerald-600">
                    <CheckCircleIcon className="h-10 w-10" />
                  </div>
                  <h3 className="mt-6 text-3xl font-semibold text-slate-950">Message sent</h3>
                  <p className="mt-3 max-w-md text-sm leading-7 text-slate-600">
                    The team has your enquiry and will follow up shortly with the right next-step discussion.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot: hidden from people, filled only by bots; the server ignores such posts. */}
                  <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
                    <label>
                      Website
                      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                    </label>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    {[
                      { name: 'name', label: 'Full name', type: 'text', required: true },
                      { name: 'email', label: 'Email address', type: 'email', required: true },
                      { name: 'company', label: 'Company', type: 'text', required: false },
                      { name: 'phone', label: 'Phone number', type: 'tel', required: false },
                    ].map((field) => (
                      <label key={field.name} className="block">
                        <span className="mb-2 block text-sm font-medium text-slate-700">
                          {field.label}
                          {field.required ? ' *' : ''}
                        </span>
                        <input
                          type={field.type}
                          name={field.name}
                          required={field.required}
                          value={formData[field.name as keyof typeof formData]}
                          onChange={handleChange}
                          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition-colors focus:border-cyan-300"
                        />
                      </label>
                    ))}
                  </div>

                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Enquiry type</span>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition-colors focus:border-cyan-300"
                    >
                      <option value="">Please select</option>
                      <option value="General Enquiry">General enquiry</option>
                      <option value="Service Enquiry">Service enquiry</option>
                      <option value="Career Opportunity">Career opportunity</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Message *</span>
                    <textarea
                      name="message"
                      rows={6}
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about the countries involved, device type, timing pressure, compliance issues, or any current pain points."
                      className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition-colors focus:border-cyan-300"
                    />
                  </label>

                  {submitError && (

                    <p role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">

                      {submitError}

                    </p>

                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0E9ED5] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0b7aa6] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sending
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-4 w-4" />
                        Send message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <SectionHeading
              eyebrow="Direct Access"
              title="Use the route that fits the urgency"
              description="For straightforward contact, use email or phone. If you are still framing the problem, the form gives more room to describe the environment."
              align="left"
            />

            <div className="grid gap-4">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.06 }}
                  className="rounded-[28px] border border-slate-200/80 bg-slate-50 p-6 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.22)]"
                >
                  <div className="flex items-start gap-4">
                    <div className="inline-flex rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-950">{item.title}</h3>
                      {item.href ? (
                        <a href={item.href} className="mt-2 inline-block text-sm font-medium text-[#2447d8] hover:text-[#0E9ED5]">
                          {item.content}
                        </a>
                      ) : (
                        <p className="mt-2 text-sm leading-7 text-slate-600">{item.content}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              {[
                { value: '24/7', label: 'Support mindset' },
                { value: '140+', label: 'Countries' },
                { value: '10+', label: 'Years' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.05 }}
                  className="rounded-[24px] border border-slate-200/80 bg-white p-4 text-center shadow-[0_18px_40px_-32px_rgba(15,23,42,0.25)]"
                >
                  <p className="font-display text-2xl font-bold text-slate-950">{stat.value}</p>
                  <p className="mt-2 text-xs text-slate-500">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            <div className="rounded-[30px] border border-slate-900/20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-7 text-white shadow-[0_28px_70px_-42px_rgba(15,23,42,0.85)]">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                <GlobeAltIcon className="h-4 w-4" />
                Global office network
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {offices.map((office) => (
                  <span key={office.country} className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-xs text-slate-200">
                    {office.country}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Global Offices"
            title="A distributed operating footprint"
            description="These offices anchor regional execution while feeding the same global operating model."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {offices.map((office, index) => (
              <motion.div
                key={office.country}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.05 }}
                className="rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.25)]"
              >
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{office.flag}</span>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-950">{office.country}</h3>
                    <p className="mt-1 text-sm font-medium text-[#2447d8]">{office.office}</p>
                    <div className="mt-4 flex items-start gap-3">
                      <MapPinIcon className="mt-1 h-4 w-4 text-slate-400" />
                      <p className="whitespace-pre-line text-sm leading-7 text-slate-600">{office.address}</p>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <PhoneIcon className="h-4 w-4 text-slate-400" />
                      <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="text-sm text-slate-600 hover:text-[#2447d8]">
                        {office.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Quick Answers"
            title="Useful starting points before the conversation"
            description="These are the questions most often raised before a first call or scoping discussion."
          />

          <div className="grid gap-6 md:grid-cols-3">
            {quickAnswers.map((item, index) => (
              <motion.div
                key={item.question}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="rounded-[28px] border border-slate-200/80 bg-slate-50 p-6 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.22)]"
              >
                <h3 className="text-xl font-semibold text-slate-950">{item.question}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{item.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
